<template>
    <ClientOnly
        v-if="
            (loggedIn && currentIdentityIsClient && displayInstantConversation) ||
            (!loggedIn && displayInstantConversationForUnloggedUsers) ||
            isInOverviewMode
        "
    >
        <VJoyDialogTrigger :dialog="instantConversationDialog">
            <VJoyButton
                data-testid="profile-instant-conversation-start-button"
                variant="secondary"
                size="small"
                full
                :disabled="(loggedIn && $ff('limit-number-of-instant-conversation-per-day') && instantConversationLimitReached) || isInOverviewMode"
            >
                {{ t('profile.header.cta.start.conversation') }}
            </VJoyButton>
        </VJoyDialogTrigger>
        <VJoyDialog
            v-if="displayInstantConversation"
            ref="instantConversationDialog"
            data-testid="profile-instant-conversation-dialog"
            size="large"
            @dialog:open="handleDialogOpen"
        >
            <template #dialog-header>{{ t('profile.instant.conversation.title') }}</template>
            <template #dialog-subheader>{{ t('profile.instant.conversation.subtitle') }}</template>
            <template #dialog-body>
                <VJoyWrapper class="instant-conversation-dialog-body">
                    <VJoyInput
                        v-model="subject"
                        data-testid="profile-instant-conversation-dialog-subject-input"
                        :label="t('profile.instant.conversation.form.subject.title')"
                        name="input-subject"
                        :placeholder="t('profile.instant.conversation.form.subject.placeholder')"
                    />
                    <VJoyTextarea
                        v-model="message"
                        data-testid="profile-instant-conversation-dialog-message-input"
                        :label="t('profile.instant.conversation.form.message.title')"
                        :placeholder="t('profile.instant.conversation.form.message.placeholder')"
                        :minlength="100"
                        :maxlength="2000"
                        :minlength-label="t('profile.instant.conversation.form.message.limit')"
                    />
                </VJoyWrapper>
            </template>
            <template #dialog-confirm>
                <VJoyButton
                    data-testid="profile-instant-conversation-dialog-confirm-button"
                    class="profile-cta-project"
                    variant="main"
                    :disabled="!validForm"
                    @click="sendMessage"
                >
                    {{ t('profile.instant.conversation.cta.confirm') }}
                </VJoyButton>
            </template>
        </VJoyDialog>
        <template #fallback>
            <VJoyFormFieldSkeleton size="small" />
        </template>
    </ClientOnly>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {navigateTo, useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {computed, useGuestHost, ref, useAuth, useDataTracking, useLocalePrefix, useTranslation} from '#imports';
    import {useABTest, useFeatureFlag} from '@malt/feature-flags';
    import {
        pushVJoySnackbar,
        VJoyButton,
        VJoyDialog,
        VJoyDialogTrigger,
        VJoyInput,
        VJoyTextarea,
        VJoyWrapper,
        VJoyFormFieldSkeleton,
    } from '@maltjoy/core-vue';
    import {FetchError} from '@api';
    import {useInstantConversationStore} from '~/store/instantConversation.store';
    import {useProfileStore} from '~/store/profileStore';

    const props = defineProps<{
        searchEventId?: string;
    }>();

    // Composables
    const {t} = useTranslation();
    const {track} = useDataTracking();
    const {withLocalePrefix} = useLocalePrefix();
    const route = useRoute();

    // Stores
    const {getVariation} = useABTest();
    const {currentIdentityIsClient, getSelectedIdentity, loggedIn} = storeToRefs(useAuth());
    const {profile} = storeToRefs(useProfileStore());
    const instantConversationStore = useInstantConversationStore();
    const {createDraftInstantConversation, createInstantConversation} = instantConversationStore;
    const {instantConversationLimitReached} = storeToRefs(instantConversationStore);

    const isClientInstantConversationEnabled = useFeatureFlag('client-instant-conversation');
    const isClientInstantConversationUnloggedEnabled = useFeatureFlag('client-instant-conversation-unlogged');

    const instantConversationDialog = useTemplateRef('instantConversationDialog');
    const subject = ref('');
    const message = ref('');

    const isInstantConversationEnabled = computed(() => getVariation('instant-conversation-v2') === 'treatment');
    const displayInstantConversation = computed(() => isClientInstantConversationEnabled.value && isInstantConversationEnabled.value);
    const displayInstantConversationForUnloggedUsers = computed(
        () => isClientInstantConversationUnloggedEnabled.value && isInstantConversationEnabled.value,
    );
    const validMessage = computed(() => message.value.length >= 100 && message.value.length <= 2000);
    const validForm = computed(() => subject.value.length !== 0 && validMessage.value);
    const isInOverviewMode = computed<boolean>(() => route?.fullPath?.includes('overview') || false);

    async function sendMessage() {
        const freelancerId = profile.value!.profileId;
        if (loggedIn.value) {
            try {
                const conversationId = await createInstantConversation({
                    clientIdentityId: getSelectedIdentity.value?.id ?? '',
                    subject: subject.value,
                    content: message.value,
                    freelancerAccountId: profile.value!.accountId,
                    searchEventId: props.searchEventId,
                });

                await track('instant_conversation_started', {
                    freelancer_id: freelancerId,
                    conversation_status: 'message_sent',
                    conversation_id: conversationId,
                    search_id: props.searchEventId,
                });

                return await goToConversation(conversationId);
            } catch (error: unknown) {
                if (error instanceof FetchError && error.statusCode === 429) {
                    pushVJoySnackbar({
                        props: {
                            message: t('profile.instant.conversation.limit.reached'),
                            level: 'error',
                        },
                    });
                } else {
                    pushVJoySnackbar({
                        props: {
                            message: t('generic.error.unexpected'),
                            level: 'error',
                        },
                    });
                }
            }
        } else {
            const draftConversationId = await createDraftInstantConversation({
                subject: subject.value,
                content: message.value,
                freelancerAccountId: profile.value!.accountId,
                searchEventId: props.searchEventId,
            });

            const host = useGuestHost();
            const redirectUriParams = encodeURIComponent(
                `${location.pathname}?action=saveInstantConversation&draftConversationId=${draftConversationId}&searchId=${
                    props.searchEventId ?? ''
                }&freelancerId=${freelancerId}`,
            );

            await track('instant_conversation_started', {
                freelancer_id: freelancerId,
                conversation_status: 'draft',
                draft_conversation_id: draftConversationId,
                search_id: props.searchEventId,
            });

            return await navigateTo(
                `${host}${withLocalePrefix(`/who-are-you?redirect=${redirectUriParams}&pageSource=profile_page&componentSource=start-instant-conversation`)}`,
                {external: true},
            );
        }
    }

    function goToConversation(conversationId: string) {
        const host = useGuestHost();

        return navigateTo(`${host}/messages/${conversationId}`, {external: true});
    }

    // Event handlers
    function handleDialogOpen() {
        track('profile_action_cta_clicked', {
            freelancer_id: profile.value!.profileId,
            action_type: 'start_instant_conversation',
            search_id: props.searchEventId,
        });
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .instant-conversation {
        &-dialog-body {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: var(--joy-core-spacing-4);
        }
    }
</style>
